/**
 * features/hr/useLeavePolicies.ts
 * ------------------------------------------------------------------
 * Live subscription to the cohort leave-entitlement policies, plus the
 * save writer. Policies are general HR config (day counts per cohort),
 * readable by any signed-in user so self-service balances resolve too;
 * only HR writes them.
 * ------------------------------------------------------------------ */
import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot, query, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { StaffCategory } from '@/lib/hr';
import {
  LEAVE_POLICY_COLLECTION,
  leavePolicyKey,
  type HrLeavePolicy,
} from '@/lib/leavePolicy';

const iso = () => new Date().toISOString();

export interface UseLeavePolicies {
  policies: HrLeavePolicy[];
  loading: boolean;
}

export function useLeavePolicies(): UseLeavePolicies {
  const [policies, setPolicies] = useState<HrLeavePolicy[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, LEAVE_POLICY_COLLECTION)),
      (snap) => {
        setPolicies(snap.docs.map((d) => ({ id: d.id, ...(d.data() as HrLeavePolicy) })));
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, []);
  return { policies, loading };
}

export async function saveLeavePolicy(
  year: string,
  category: StaffCategory,
  entitlements: Record<string, number>,
): Promise<void> {
  await setDoc(
    doc(db, LEAVE_POLICY_COLLECTION, leavePolicyKey(year, category)),
    { year, category, entitlements, updatedAt: iso() },
    { merge: true },
  );
}
